package com.example.mystybag.appCore.logic.addMerchantLocation

import com.example.mystybag.appCore.model.addMerchantLocation.MerchantRegistrationMenuPhotosUIState
import com.example.mystybag.common.logic.BSNavigatorChooseTakePhotoOrPickFile
import com.example.mystybag.common.logic.ChooseTakePhotoOrPickFileBottomSheetChild
import com.example.mystybag.common.model.ChooseTakePhotoOrPickFileVisualModel
import com.example.mystybag.core.expected.PlatformHelperWrapper.openGallery
import com.example.mystybag.core.expected.PlatformHelperWrapper.takePhoto
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.BottomSheetChild
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.UIBottomSheetConfig
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.common.resources.SR
import com.example.mystybag.cropImage.logic.NavigatorProfileCropImage
import com.example.mystybag.cropImage.logic.ProfileImageCropperCallbacks
import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.defines.FileUploadTarget
import com.example.mystybag.communication.ext.CommandResult
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.byteArrayFromFile
import com.example.mystybag.cropImage.expected.getTempImageBytes
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.login.logic.NavigatorAddFirstBagPrompt
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep9B
import com.example.mystybag.permissionsUtils.expected.Permission
import com.example.mystybag.permissionsUtils.expected.PermissionHelper.requestPermission
import com.example.mystybag.permissionsUtils.logic.DialogNavigatorPermissionSettings
import com.example.mystybag.sharedmodels.SCImage
import com.example.mystybag.appCore.logic.NavigatorMerchantLocationsList
import com.example.mystybag.sharedmodels.project.requestModels.merchant.CreateMerchantLocationRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.UpdateMerchantLocationRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.parseLegacyProductsString
import com.example.mystybag.sharedmodels.project.responseModels.merchant.CreateMerchantLocationResponseModel
import kotlinx.coroutines.launch

/** Max menu photos per location (same as DB: merchant_location_menu_photos). */
const val MAX_MENU_PHOTOS = 10

/** Thumbnails per row in add / edit location menu photo grids (Figma: 3). */
const val MENU_PHOTOS_GRID_COLUMNS = 3

/** Menu photo slot and exported crop size (width × height px / dp shape). */
const val MENU_PHOTO_OUTPUT_WIDTH = 121
const val MENU_PHOTO_OUTPUT_HEIGHT = 140
const val MENU_PHOTO_ASPECT_RATIO = 121f / 140f

class MerchantRegistrationMenuPhotosViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep9B
) : ChildViewModel<MerchantRegistrationMenuPhotosUIState>(context, MerchantRegistrationMenuPhotosUIState()) {

    override fun onCreate() {
        navControls.backButtonVisible = true
    }

    override fun configureBottomSheets(config: UIBottomSheetConfig, context: AppContext): BottomSheetChild = when (config) {
        is BSNavigatorChooseTakePhotoOrPickFile -> ChooseTakePhotoOrPickFileBottomSheetChild(
            title               = sharedString(SR.strings.add_menu_photo),
            infoText            = listOf(),
            onInfoButtonClicked = { },
            additionalRows = arrayListOf(
                ChooseTakePhotoOrPickFileVisualModel(
                    text    = sharedString(SR.strings.take_photo),
                    image   = SRC.images.camera_fill_icon,
                    onClick = {
                        closeBottomSheet {
                            launchOnMain {
                                onCameraPermissionResponse(requestPermission(Permission.CaptureImage))
                            }
                        }
                    }
                ),
                ChooseTakePhotoOrPickFileVisualModel(
                    text    = sharedString(SR.strings.open_gallery),
                    image   = SRC.images.gallery_fill_icon,
                    onClick = {
                        closeBottomSheet {
                            launchOnMain { onGalleryPermissionResponse(requestPermission(Permission.OpenGallery)) }
                        }
                    }
                )
            )
        )
        else -> super.configureBottomSheets(config, context)
    }

    fun onAddMenuPhotoClicked() {
        val existingCount = config.existingMenuPhotoUrls.size
        if (existingCount + uiState.value.menuPhotoFileNames.size >= MAX_MENU_PHOTOS) return
        if (com.example.mystybag.core.expected.PlatformHelper.isWeb) {
            // On Web: open file picker directly (no bottom sheet); input.click() must run in user gesture context
            scope.launch {
                val fileModel = openGallery()
                launchOnMain {
                    fileModel?.image?.let { image -> cropImage(image) }
                }
            }
        } else {
            // On Android/iOS: same as cover photo – bottom sheet "Take photo" / "Open gallery"
            openBottomSheet(BSNavigatorChooseTakePhotoOrPickFile)
        }
    }

    private fun onGalleryPermissionResponse(isGranted: Boolean) {
        if (isGranted)
            launchOnMain { openGallery()?.image?.let { image -> cropImage(image) } }
        else
            showPermissionDialog(false)
    }

    private fun onCameraPermissionResponse(isGranted: Boolean) {
        if (isGranted)
            launchOnMain { takePhoto()?.image?.let { cropImage(it) } }
        else
            showPermissionDialog(true)
    }

    private fun cropImage(image: SCImage) {
        navigate(NavigatorProfileCropImage(
            selectedImage = image,
            callbacks = object : ProfileImageCropperCallbacks {
                override fun onImageCropped(imageFileName: String, originalImgFileName: String) {
                    goBack()
                    updateState { state ->
                        if (state.menuPhotoFileNames.size >= MAX_MENU_PHOTOS) state
                        else state.copy(menuPhotoFileNames = state.menuPhotoFileNames + imageFileName)
                    }
                }
            },
            aspectRatio = MENU_PHOTO_ASPECT_RATIO,
            outputWidth = MENU_PHOTO_OUTPUT_WIDTH,
            outputHeight = MENU_PHOTO_OUTPUT_HEIGHT,
        ))
    }

    private fun showPermissionDialog(isForCamera: Boolean) {
        uiThread {
            showDialog(
                DialogNavigatorPermissionSettings(
                    sharedString(
                        if (isForCamera)
                            SR.strings.camera_permission
                        else
                            SR.strings.gallery_permission
                    )
                )
            )
                .onPositiveClick {
                    com.example.mystybag.core.expected.openSettings()
                }
        }
    }

    fun removeMenuPhotoAt(index: Int) {
        updateState { state ->
            if (index !in state.menuPhotoFileNames.indices) state
            else state.copy(menuPhotoFileNames = state.menuPhotoFileNames.filterIndexed { i, _ -> i != index })
        }
    }

    fun onContinue() {
        scope.launch {
            updateState { it.copy(isLoading = true, errorMessage = null) }

            commandBlock(showProgress = true) {
                var coverPhotoUrl: String? = config.selectedPresetCoverImageUrl?.takeIf { it.isNotBlank() }

                config.locationCoverImageFileName?.let { fileName ->
                    val bytes = byteArrayFromFile(fileName)
                        ?: getTempImageBytes(fileName)
                    if (bytes != null) {
                        val res = Communication.uploadFile(FileUploadTarget.MERCHANT_LOCATIONS, bytes, "cover.jpg")
                        when (res) {
                            is CommandResult.Success -> coverPhotoUrl = res.successModel.url
                            else -> {
                                val err = (res as? CommandResult.BaseError)?.baseErrorModel
                                launchOnMain {
                                    updateState { it.copy(isLoading = false) }
                                    showSnackbar(Response.getErrorMessage(err?.status ?: -1, err?.apiEndPoint ?: "", err?.statusMessage))
                                }
                                return@commandBlock
                            }
                        }
                    }
                }

                val menuPhotoUrlsToAdd = mutableListOf<String>()
                for ((index, fileName) in uiState.value.menuPhotoFileNames.withIndex()) {
                    val bytes = byteArrayFromFile(fileName)
                        ?: getTempImageBytes(fileName)
                    if (bytes != null) {
                        val res = Communication.uploadFile(FileUploadTarget.MERCHANT_LOCATION_MENU_PHOTOS, bytes, "menu_$index.jpg")
                        when (res) {
                            is CommandResult.Success -> menuPhotoUrlsToAdd.add(res.successModel.url)
                            else -> {
                                val err = (res as? CommandResult.BaseError)?.baseErrorModel
                                launchOnMain {
                                    updateState { it.copy(isLoading = false) }
                                    showSnackbar(Response.getErrorMessage(err?.status ?: -1, err?.apiEndPoint ?: "", err?.statusMessage))
                                }
                                return@commandBlock
                            }
                        }
                    }
                }

                val finalCoverUrlForUpdate = coverPhotoUrl ?: config.existingCoverPhotoUrl
                val locationId = config.locationId
                if (locationId != null) {
                    // Edit flow: update existing location (same fields as create)
                    CommonCommands.updateMerchantLocation(
                        UpdateMerchantLocationRequestModel(
                            locationId = locationId,
                            businessCategoryId = config.businessCategoryId,
                            storeName = config.storeName,
                            storeAddress = config.storeAddress.trim(),
                            cityId = config.cityId,
                            storePhoneNumber = config.storePhoneNumber.filter { it.isDigit() },
                            dialCode = config.dialCode.trim(),
                            packaging = config.packaging,
                            additionalPickupInstructions = config.additionalPickupInstructions,
                            surpriseBagContainsBuffetFood = config.surpriseBagContainsBuffetFood,
                            latitude = config.latitude,
                            longitude = config.longitude,
                            products = parseLegacyProductsString(config.whatYouCouldGet),
                            coverPhotoUrl = finalCoverUrlForUpdate,
                            menuPhotoUrlsToKeep = config.existingMenuPhotoUrls.filter { it.isNotBlank() },
                            menuPhotoUrlsToAdd = menuPhotoUrlsToAdd
                        )
                    )
                        .onResponse(
                            success = {
                                launchOnMain {
                                    updateState { it.copy(isLoading = false) }
                                    setResult<MainCallback> {
                                        popToMainAndNavigate(NavigatorMerchantLocationsList)
                                    }
                                }
                            },
                            baseError = { response ->
                                launchOnMain {
                                    updateState {
                                        it.copy(
                                            isLoading = false,
                                            errorMessage = Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage)
                                        )
                                    }
                                }
                            }
                        )
                } else {
                    val coverForCreate = coverPhotoUrl ?: config.existingCoverPhotoUrl
                    if (coverForCreate.isNullOrBlank()) {
                        launchOnMain {
                            updateState { it.copy(isLoading = false) }
                            showSnackbar(sharedString(SR.strings.please_select_business_cover_image))
                        }
                        return@commandBlock
                    }
                    // Create flow: new location
                    CommonCommands.createMerchantLocation(
                        CreateMerchantLocationRequestModel(
                            merchantId = config.merchantId,
                            businessCategoryId = config.businessCategoryId,
                            storeName = config.storeName,
                            storeAddress = config.storeAddress.trim(),
                            cityId = config.cityId,
                            storePhoneNumber = config.storePhoneNumber.filter { it.isDigit() },
                            dialCode = config.dialCode.trim(),
                            packaging = config.packaging,
                            additionalPickupInstructions = config.additionalPickupInstructions,
                            surpriseBagContainsBuffetFood = config.surpriseBagContainsBuffetFood,
                            latitude = config.latitude,
                            longitude = config.longitude,
                            products = parseLegacyProductsString(config.whatYouCouldGet),
                            coverPhotoUrl = coverForCreate,
                            menuPhotoUrls = menuPhotoUrlsToAdd
                        )
                    )
                        .onResponse(
                            success = { response ->
                                launchOnMain {
                                    val responseModel = response as? CreateMerchantLocationResponseModel
                                    updateState { it.copy(isLoading = false) }
                                    setResult<MainCallback> {
                                        popToMainAndNavigate(
                                            NavigatorAddFirstBagPrompt(
                                                merchantLocationId = responseModel?.merchantLocationId ?: 0L,
                                                businessCategoryId = config.businessCategoryId,
                                                returnToDashboard = false // Stay on Locations tab after adding location
                                            )
                                        )
                                    }
                                }
                            },
                            baseError = { response ->
                                launchOnMain {
                                    updateState {
                                        it.copy(
                                            isLoading = false,
                                            errorMessage = Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage)
                                        )
                                    }
                                }
                            }
                        )
                }
            }
        }
    }
}
